import React, { createContext, useContext, useState, useCallback } from 'react';
import { OCRResult, ExtractedField } from '../services/api/ocr.service';

export interface FileWithPreview {
  file: File;
  preview: string;
  driveFileId?: string; // Google Drive file ID for renaming
  parentFolderId?: string; // Google Drive parent folder ID for renaming folder
}

export interface ProcessingItem {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'complete' | 'error';
  error?: string;
}

export interface DocumentResult {
  fileIndex: number;
  fileName: string;
  result: OCRResult;
  editedFields: ExtractedField[];
  driveFileId?: string; // Google Drive file ID for renaming
  parentFolderId?: string; // Google Drive parent folder ID for renaming folder
}

interface UploadState {
  files: FileWithPreview[];
  extractedData: OCRResult | null;
  editedFields: ExtractedField[];
  // Processing state (OCR extraction)
  isProcessing: boolean;
  processingItems: ProcessingItem[];
  allResults: DocumentResult[];
  // Download state (Google Drive downloads)
  isDownloading: boolean;
  downloadingItems: ProcessingItem[];
  // Export state (Google Sheets export)
  isExporting: boolean;
  exportingItems: ProcessingItem[];
}

interface UploadContextType extends UploadState {
  setFiles: (files: FileWithPreview[] | ((prev: FileWithPreview[]) => FileWithPreview[])) => void;
  setExtractedData: (data: OCRResult | null) => void;
  setEditedFields: (fields: ExtractedField[] | ((prev: ExtractedField[]) => ExtractedField[])) => void;
  clearAll: () => void;
  addFiles: (newFiles: FileWithPreview[]) => void;
  removeFile: (index: number) => void;
  // Processing methods (OCR extraction)
  setIsProcessing: (value: boolean) => void;
  setProcessingItems: (items: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => void;
  setAllResults: (results: DocumentResult[] | ((prev: DocumentResult[]) => DocumentResult[])) => void;
  closeProcessingProgress: () => void;
  // Download methods (Google Drive downloads)
  setIsDownloading: (value: boolean) => void;
  setDownloadingItems: (items: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => void;
  closeDownloadProgress: () => void;
  // Export methods (Google Sheets export)
  setIsExporting: (value: boolean) => void;
  setExportingItems: (items: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => void;
  closeExportProgress: () => void;
}

const UploadContext = createContext<UploadContextType | null>(null);

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [files, setFilesState] = useState<FileWithPreview[]>([]);
  const [extractedData, setExtractedData] = useState<OCRResult | null>(null);
  const [editedFields, setEditedFieldsState] = useState<ExtractedField[]>([]);

  // Processing state (OCR extraction)
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingItems, setProcessingItemsState] = useState<ProcessingItem[]>([]);
  const [allResults, setAllResultsState] = useState<DocumentResult[]>([]);

  // Download state (Google Drive downloads)
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingItems, setDownloadingItemsState] = useState<ProcessingItem[]>([]);

  // Export state (Google Sheets export)
  const [isExporting, setIsExporting] = useState(false);
  const [exportingItems, setExportingItemsState] = useState<ProcessingItem[]>([]);

  const setFiles = useCallback((filesOrFn: FileWithPreview[] | ((prev: FileWithPreview[]) => FileWithPreview[])) => {
    if (typeof filesOrFn === 'function') {
      setFilesState(filesOrFn);
    } else {
      setFilesState(filesOrFn);
    }
  }, []);

  const setEditedFields = useCallback((fieldsOrFn: ExtractedField[] | ((prev: ExtractedField[]) => ExtractedField[])) => {
    if (typeof fieldsOrFn === 'function') {
      setEditedFieldsState(fieldsOrFn);
    } else {
      setEditedFieldsState(fieldsOrFn);
    }
  }, []);

  const setProcessingItems = useCallback((itemsOrFn: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => {
    if (typeof itemsOrFn === 'function') {
      setProcessingItemsState(itemsOrFn);
    } else {
      setProcessingItemsState(itemsOrFn);
    }
  }, []);

  const setAllResults = useCallback((resultsOrFn: DocumentResult[] | ((prev: DocumentResult[]) => DocumentResult[])) => {
    if (typeof resultsOrFn === 'function') {
      setAllResultsState(resultsOrFn);
    } else {
      setAllResultsState(resultsOrFn);
    }
  }, []);

  const addFiles = useCallback((newFiles: FileWithPreview[]) => {
    setFilesState(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setFilesState(prev => {
      const file = prev[index];
      if (file?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearAll = useCallback(() => {
    files.forEach(f => {
      if (f.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(f.preview);
      }
    });
    setFilesState([]);
    setExtractedData(null);
    setEditedFieldsState([]);
    setAllResultsState([]);
  }, [files]);

  const closeProcessingProgress = useCallback(() => {
    setProcessingItemsState([]);
  }, []);

  const setDownloadingItems = useCallback((itemsOrFn: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => {
    if (typeof itemsOrFn === 'function') {
      setDownloadingItemsState(itemsOrFn);
    } else {
      setDownloadingItemsState(itemsOrFn);
    }
  }, []);

  const closeDownloadProgress = useCallback(() => {
    setDownloadingItemsState([]);
  }, []);

  const setExportingItems = useCallback((itemsOrFn: ProcessingItem[] | ((prev: ProcessingItem[]) => ProcessingItem[])) => {
    if (typeof itemsOrFn === 'function') {
      setExportingItemsState(itemsOrFn);
    } else {
      setExportingItemsState(itemsOrFn);
    }
  }, []);

  const closeExportProgress = useCallback(() => {
    setExportingItemsState([]);
  }, []);

  return (
    <UploadContext.Provider
      value={{
        files,
        extractedData,
        editedFields,
        isProcessing,
        processingItems,
        allResults,
        isDownloading,
        downloadingItems,
        setFiles,
        setExtractedData,
        setEditedFields,
        clearAll,
        addFiles,
        removeFile,
        setIsProcessing,
        setProcessingItems,
        setAllResults,
        closeProcessingProgress,
        setIsDownloading,
        setDownloadingItems,
        closeDownloadProgress,
        isExporting,
        exportingItems,
        setIsExporting,
        setExportingItems,
        closeExportProgress,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within UploadProvider');
  }
  return context;
};
